'use client';

import { type FC, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Textarea } from '@paypoteam/ui-components';
import { CaretLeft } from '@phosphor-icons/react';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/navigation';
import { type z } from 'zod';

import {
    pushBusinessFormFieldFocusGTMEvent,
    pushBusinessFormFirstStepFinishGTMEvent,
    pushBusinessFormStatusGTMEvent,
} from '@/lib/gtm';
import {
    postBusinessContactForm,
    postBusinessContactFormToN8N,
} from '@/services/business-contact-form';

import { Input } from '@/components/atoms/Input';
import { PhoneNumberInput } from '@/components/atoms/PhoneNumberInput';
import { RadioGroup } from '@/components/atoms/RadioGroup';
import { P } from '@/components/atoms/Typography/P';
import { Title } from '@/components/atoms/Typography/Title';
import {
    FormControl,
    FormField,
    FormFieldWrapper,
    FormItem,
    FormMessage,
    FormProvider,
} from '@/components/molecules/Form';
import Stepper from '@/components/molecules/Stepper';
import GooglePoliciesInfo from '@/components/organisms/GooglePoliciesInfo';

import DataProtectionInfo from './components/DataProtectionInfo';
import PaymentGatewayCards from './components/PaymentGatewayCards';
import {
    ANNUAL_CASH_FLOW_VALUES,
    ERROR_MESSAGES_CONFIG,
    FIRST_FORM_STEP_FIELDS,
    SECOND_FORM_STEP_FIELDS,
} from './static/formStaticDataConfig';
import {
    BusinessContactFormSchema,
    type BusinessContactFormValuesType,
} from './utils/BusinessContactFormSchema';
import { mapFormDataToMultipartFormat } from './utils/mapFormDataToMultipartFormat';
import { mapRadioGroupConfigData } from './utils/mapRadioGroupConfigData';
import {
    type BusinessContactFormProps,
    type FormInitialValuesType,
} from './BusinessContactForm.types';

const BussinessContactForm: FC<BusinessContactFormProps> = ({
    dataProtectionInfoContent,
    isStepperVisible,
}) => {
    const [activeFormStep, setActiveFormStep] = useState<number>(1);
    const [globalFormError, setGlobalFormError] = useState<string>('');

    const router = useRouter();

    const { executeRecaptcha } = useGoogleReCaptcha();

    const formInitialValues: FormInitialValuesType = {
        name: '',
        email: '',
        phone_number: '',
        nip: '',
        website: '',
        annual_cash_flow: 'brak danych',
        payment_gateway: 'Inne',
        message: '',
        'g-recaptcha-response': '',
    };

    const formProviderConfig = useForm<
        z.infer<typeof BusinessContactFormSchema>
    >({
        mode: 'all',
        resolver: zodResolver(BusinessContactFormSchema),
        defaultValues: formInitialValues,
    });

    const {
        getValues,
        setValue,
        register,
        formState: { errors },
    } = formProviderConfig;

    const isFormStepValid = (formStepFields: (keyof FormInitialValuesType)[]) =>
        formStepFields.every(fieldName => {
            const value = getValues(fieldName);
            const hasError = !!errors[fieldName];

            return value && !hasError;
        });

    const handleFieldFocus = (fieldName: string) =>
        pushBusinessFormFieldFocusGTMEvent(fieldName);

    const handleFormStepChange = (): void => {
        activeFormStep === 1 && pushBusinessFormFirstStepFinishGTMEvent();
        setActiveFormStep(prevState => (prevState === 1 ? 2 : 1));
    };

    const handleFormErrorOccurred = (errorMessage: string) => {
        pushBusinessFormStatusGTMEvent('formError', 'Contact');

        Sentry.captureException({
            message: `Business contact form exception: ${errorMessage}`,
            country: 'PL',
            page: 'biznes',
        });

        setGlobalFormError(ERROR_MESSAGES_CONFIG.global);
    };

    const onSubmit = async (): Promise<void> => {
        if (!executeRecaptcha) {
            throw new Error('Nie udało się załadować tokena reCaptcha');
        }

        const captchaToken = await executeRecaptcha();
        setValue('g-recaptcha-response', captchaToken);

        const formValues = getValues();
        const mappedFormValues = mapFormDataToMultipartFormat(formValues);
        mappedFormValues.append('agreements', JSON.stringify({}));
        mappedFormValues.append('source', 'biznes');

        try {
            const response = await postBusinessContactForm(mappedFormValues);

            if (response.status === 200) {
                pushBusinessFormStatusGTMEvent('formSubmitted', 'Contact');

                const n8nResponse =
                    await postBusinessContactFormToN8N(mappedFormValues);

                if (n8nResponse.status !== 200) {
                    Sentry.captureException({
                        message: `N8N communication exception: ${n8nResponse.message}`,
                        country: 'PL',
                        page: 'biznes',
                    });
                }

                setGlobalFormError('');

                router.push('/biznes/dziekujemy-za-zgloszenie/');
            } else {
                handleFormErrorOccurred(response.message);
            }
        } catch (error) {
            const errorMessage = (error as Error).message;

            handleFormErrorOccurred(errorMessage);
        }
    };

    return (
        <>
            {isStepperVisible && (
                <Stepper
                    stepsNumber={2}
                    activeStep={activeFormStep}
                    className="mb-8"
                />
            )}

            <FormProvider {...formProviderConfig}>
                <form onSubmit={formProviderConfig.handleSubmit(onSubmit)}>
                    {activeFormStep === 1 && (
                        <>
                            <Title size="m">Podaj swoje dane (wymagane)</Title>

                            <FormFieldWrapper
                                name="name"
                                label="Imię i nazwisko"
                            >
                                <Input
                                    data-test-id="name-input"
                                    onFocus={e =>
                                        handleFieldFocus(e.target.name)
                                    }
                                />
                            </FormFieldWrapper>

                            <FormFieldWrapper
                                name="email"
                                label="E-mail"
                            >
                                <Input
                                    data-test-id="email-input"
                                    onFocus={e =>
                                        handleFieldFocus(e.target.name)
                                    }
                                />
                            </FormFieldWrapper>

                            <FormFieldWrapper
                                name="phone_number"
                                label="Numer telefonu"
                            >
                                <PhoneNumberInput
                                    data-test-id="phone-number-input"
                                    onFocus={e =>
                                        handleFieldFocus(e.target.name)
                                    }
                                />
                            </FormFieldWrapper>

                            <FormFieldWrapper
                                name="nip"
                                label="NIP"
                            >
                                <Input
                                    data-test-id="nip-input"
                                    onFocus={e =>
                                        handleFieldFocus(e.target.name)
                                    }
                                />
                            </FormFieldWrapper>

                            <DataProtectionInfo
                                config={dataProtectionInfoContent}
                                className="mb-8 mt-1"
                            />

                            <Button
                                onClick={handleFormStepChange}
                                className="mx-auto w-full md:w-fit"
                                data-test-id="button-business-contact-form-next-step"
                                disabled={
                                    !isFormStepValid(FIRST_FORM_STEP_FIELDS)
                                }
                                aria-disabled={
                                    !isFormStepValid(FIRST_FORM_STEP_FIELDS)
                                }
                            >
                                Przejdź dalej
                            </Button>
                        </>
                    )}

                    {activeFormStep === 2 && (
                        <>
                            <Title size="m">Strona www (wymagane)</Title>

                            <FormFieldWrapper
                                name="website"
                                label="Adres strony www"
                            >
                                <Input
                                    data-test-id="website-input"
                                    onFocus={e =>
                                        handleFieldFocus(e.target.name)
                                    }
                                />
                            </FormFieldWrapper>

                            <Title
                                size="m"
                                className="mt-5"
                            >
                                Roczny obrót
                            </Title>

                            <FormField
                                name={'annual_cash_flow'}
                                render={({ field }) => (
                                    <FormItem className="py-3">
                                        <FormControl>
                                            <RadioGroup
                                                config={mapRadioGroupConfigData(
                                                    ANNUAL_CASH_FLOW_VALUES,
                                                )}
                                                onValueChange={value => {
                                                    setValue(
                                                        'annual_cash_flow',
                                                        value as BusinessContactFormValuesType['annual_cash_flow'],
                                                    );
                                                    handleFieldFocus(
                                                        'annual_cash_flow',
                                                    );
                                                }}
                                                value={field.value}
                                                className="mt-4 flex flex-col flex-wrap md:flex-row"
                                            />
                                        </FormControl>

                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <PaymentGatewayCards
                                defaultCard={formInitialValues.payment_gateway}
                                formProviderConfig={formProviderConfig}
                                setCardName={handleFieldFocus}
                            />

                            <Title
                                size="m"
                                className="mb-1 mt-5"
                            >
                                Wiadomość
                            </Title>

                            <FormFieldWrapper
                                name="message"
                                label="Treść wiadomości..."
                            >
                                <Textarea
                                    data-test-id={'message-input'}
                                    onFocus={e =>
                                        handleFieldFocus(e.target.name)
                                    }
                                />
                            </FormFieldWrapper>

                            <div className="mt-5 flex justify-between">
                                <Button
                                    onClick={handleFormStepChange}
                                    variant="ghost"
                                >
                                    <CaretLeft size={16} />
                                    Powrót
                                </Button>

                                <Button
                                    type="submit"
                                    data-test-id="button-business-contact-form-send"
                                    disabled={
                                        !isFormStepValid(
                                            SECOND_FORM_STEP_FIELDS,
                                        )
                                    }
                                    aria-disabled={
                                        !isFormStepValid(
                                            SECOND_FORM_STEP_FIELDS,
                                        )
                                    }
                                >
                                    Wyślij formularz
                                </Button>
                            </div>

                            {globalFormError && (
                                <P className="mt-8 text-center text-red-800">
                                    {globalFormError}
                                </P>
                            )}
                        </>
                    )}

                    <input
                        {...register('g-recaptcha-response')}
                        className="hidden"
                    />
                </form>
            </FormProvider>

            <GooglePoliciesInfo className="mt-8" />
        </>
    );
};

export default BussinessContactForm;
